// ============================================================
// Centralised number formatters (UI-only, no data-layer change).
// Import these in any page/component instead of rolling your own.
// ============================================================

/**
 * Whole-integer INR currency. Rounds to nearest integer, Indian grouping.
 *   fmtINR(54698.0000001) → "₹54,698"
 *   fmtINR(null)          → "₹0"
 *   fmtINR(null, '—')     → "—"
 */
export const fmtINR = (v, fallback = '₹0') => {
  if (v == null || isNaN(v)) return fallback;
  return `₹${Math.round(Number(v)).toLocaleString('en-IN')}`;
};

/**
 * Whole-integer number (no currency). For covers, days, counts.
 *   fmtInt(2213.4) → "2,213"
 */
export const fmtInt = (v, fallback = '0') => {
  if (v == null || isNaN(v)) return fallback;
  return Math.round(Number(v)).toLocaleString('en-IN');
};

/**
 * Percentage — max 1 decimal, trailing zero stripped for whole numbers.
 *   fmtPct(86.27) → "86.3%"
 *   fmtPct(86.0)  → "86%"
 *   fmtPct(null)  → "—"
 */
export const fmtPct = (v, fallback = '—') => {
  if (v == null || isNaN(v)) return fallback;
  const n = Number(v);
  const rounded = Math.round(n * 10) / 10;
  // Strip trailing .0 (so 86.0 → "86", 86.3 → "86.3")
  const s = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${s}%`;
};

/**
 * Quantity with unit — up to 2 decimals, trailing zeros stripped.
 *   fmtQty(2.5, 'kg')   → "2.5 kg"
 *   fmtQty(2.0, 'kg')   → "2 kg"
 *   fmtQty(2.456, 'kg') → "2.46 kg"
 *   fmtQty(2, 'pcs')    → "2 pcs"
 */
export const fmtQty = (v, unit = '', fallback = '—') => {
  if (v == null || isNaN(v)) return fallback;
  const n = Number(v);
  // Round to 2dp then drop trailing zeros via parseFloat
  const cleaned = parseFloat(n.toFixed(2));
  const s = cleaned.toLocaleString('en-IN');
  return unit ? `${s} ${unit}` : s;
};

/**
 * Abbreviated INR for tight spaces (L = lakh, K = thousand).
 *   fmtShort(1275234) → "₹12.8L"
 *   fmtShort(54698)   → "₹55K"
 *   fmtShort(750)     → "₹750"
 */
export const fmtShort = (v, fallback = '₹0') => {
  if (v == null || isNaN(v)) return fallback;
  const n = Number(v);
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.0$/, '')}L`;
  if (Math.abs(n) >= 1000) return `₹${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `₹${Math.round(n)}`;
};

/**
 * Signed delta for variance displays.
 *   fmtDelta(5000)  → "+₹5,000"
 *   fmtDelta(-5000) → "−₹5,000"  (note: minus sign, not hyphen, for tabular alignment)
 */
export const fmtDelta = (v, fallback = '₹0') => {
  if (v == null || isNaN(v)) return fallback;
  const n = Math.round(Number(v));
  const sign = n >= 0 ? '+' : '−';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN')}`;
};
